// import external modules
import { useReactiveVar } from '@apollo/client';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// import icons
import {
  AccountBox as AccountBoxIcon,
  Chat as ChatIcon,
  ExitToApp as ExitToAppIcon,
  ArrowDropUp as ExpandLess,
  ArrowDropDown as ExpandMore,
  HelpOutline as HelpOutlineIcon,
  ArrowLeft as LeftIcon,
  LiveHelp as LiveHelpIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

// relative imports
import {
  primaryAccountIdVar,
  switchingAccountsVar,
} from '../../../data/apollo/cache/reactiveVars';
import {
  getAppConfig,
  isFeatureEnabled,
} from '../../../data/app-config/configs/configUtilities';
import UpdateDataSourcesButton from '../../account-management/account-updates/UpdateAccountDataButton';
import TrialUpgradeButton from '../../billing/subscriptions/TrialUpgradeButton';
import PendingCodeInviteButton from '../../client-management/PendingCodeInviteButton';
import { useAccountConfig } from '../../providers/AccountConfigProvider';
import { useAccount } from '../../providers/AccountProvider';
import { useLedgibleAuth } from '../../providers/LedgibleAuthProvider';
import PaymentSourceExpiryAlert from '../custom-components/PaymentSourceExpiryAlert';
import useDisplayConfig from '../custom-hooks/useDisplayConfig';
import usePaymentSourceExpiry from '../custom-hooks/usePaymentSourceExpiry';
import { openZendeskWidget } from '../customer-support/zendesk-operations';
import { getAppLogoSrc } from '../utilities/appUtilities';
import AccountWindow from './account-window/AccountWindow';
import AdminClientViewBar from './AdminClientViewBar';
import { appTabs } from './SecondaryToolbar';

const useStyles = makeStyles()((theme) => ({
  appBar: {
    backgroundColor:
      theme.whiteLabel.Header.color || theme.palette.secondary.main,
  },
  toolbarMain: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    lineHeight: '40px',
    minHeight: '40px',
    width: '100%',
  },
  backButton: {
    color: theme.palette.grey[100],
    marginLeft: theme.spacing(2),
    '&:hover': {
      color: theme.palette.grey[300],
      backgroundColor: 'transparent',
    },
  },
  tabIcon: {
    fontSize: '1.15rem',
  },
  moreBtnIcon: {
    fontSize: theme.typography.pxToRem(26),
    color: '#fff',
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  menuItem: {
    padding: 0,
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
  menuList: {
    width: '225px',
  },
  boxGrid: {
    minHeight: 56,
  },
  notificationsButton: {
    color: theme.palette.common.white,
    padding: 5,
    marginRight: theme.spacing(1),
    marginTop: -1,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    boxShadow:
      '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.3)',
    },
  },
  notificationsIcon: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.common.white,
  },
  updateDataButton: {
    marginTop: -3,
  },
  productName: {
    fontSize: theme.typography.pxToRem(18),
    lineHeight: '30px',
    fontWeight: 800,
    color: theme.whiteLabel.ProductName.color || 'inherit',
  },
  productNameDivider: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: '2px',
    height: '30px',
    backgroundColor: theme.palette.grey[700],
  },
  listItem: {
    justifyContent: 'center',
    color: theme.whiteLabel.SecondaryToolbar.textColor || 'inherit',
  },
  listItemIcon: {
    color: theme.whiteLabel.SecondaryToolbar.textColor || 'inherit',
  },
}));

const supportListItem = [
  {
    icon: <LiveHelpIcon fontSize="small" />,
    title: 'Help',
    link: 'https://verady.zendesk.com/hc/en-us#360002561431',
  },
  {
    icon: <ChatIcon fontSize="small" />,
    title: 'Contact Us',
  },
];

const validTrialUrls = [`/trial-expired`, `/administration/change-plan`];

const MobileHeader = ({
  hideAccountMenu,
  hideUpdateButton,
  setHeaderHeight,
}) => {
  const appConfig = getAppConfig();
  const { accountConfig } = useAccountConfig();
  const { tabLabels } = useDisplayConfig(accountConfig);
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const headerRef = useRef();
  const primaryAccountId = useReactiveVar(primaryAccountIdVar);
  const switchingAccounts = useReactiveVar(switchingAccountsVar);
  const { show: showExpiryBar } = usePaymentSourceExpiry();

  useEffect(() => {
    setHeaderHeight(headerRef.current.clientHeight);
  });

  const [supportListOpen, setSupportListOpen] = useState(false);
  const handleSupportListClick = () => setSupportListOpen(!supportListOpen);

  const { state: locationState, pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useLedgibleAuth();
  const { currentAccount, clientPermissions: permissions } = useAccount();
  const { audit, chainalysisIntegrationActive, chainalysisIntegrationEnabled } =
    currentAccount;

  // generate array from appTabs and filter out any tabs with hidden property based on
  // any conditions applied, can pass args to appTabs
  const visibleListItems = useMemo(() => {
    const tabSet = accountConfig?.enabledPageIds || [];

    return appTabs({
      audit,
      monitoringActive:
        chainalysisIntegrationEnabled && chainalysisIntegrationActive,
      accountId: currentAccount.id,
      tabLabels,
      isAdminViewingClient: permissions?.isAdminViewingClient,
    }).filter((tab) => tabSet.includes(tab.id) && !tab.hidden);
  }, [
    accountConfig?.enabledPageIds,
    audit,
    chainalysisIntegrationActive,
    chainalysisIntegrationEnabled,
    currentAccount.id,
    permissions?.isAdminViewingClient,
    tabLabels,
  ]);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    handleClose();
  };

  return (
    <AppBar className={classes.appBar} position="fixed" ref={headerRef}>
      {permissions?.isAdminViewingClient && !switchingAccounts && (
        <AdminClientViewBar />
      )}
      {showExpiryBar && <PaymentSourceExpiryAlert />}
      <div className={classes.toolbarMain}>
        <Box display="flex">
          <Box
            display="flex"
            flexGrow={1}
            alignItems="center"
            paddingLeft={1}
            className={classes.boxGrid}
            onClick={() => {
              navigate({
                to: appConfig.alternateBaseAppPath || `/`,
              });
            }}
          >
            <img src={getAppLogoSrc()} alt="Ledgible" height="35px" />
            {appConfig.productName && (
              <>
                <Divider
                  className={classes.productNameDivider}
                  orientation="vertical"
                />
                <Typography className={classes.productName}>
                  {appConfig.productName}
                </Typography>
              </>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            paddingRight={1}
            className={classes.boxGrid}
          >
            {hideAccountMenu &&
              currentAccount.id &&
              locationState &&
              locationState.redirect && (
                <Button
                  component={Link}
                  to={locationState.redirect}
                  className={classes.backButton}
                  variant="text"
                  id="TopNav-BackToDashboardWithRedirectBtn"
                >
                  <LeftIcon />
                  {locationState.redirectLabel}
                </Button>
              )}
            {!hideAccountMenu &&
              !hideUpdateButton &&
              currentAccount.id &&
              (isFeatureEnabled({
                moduleName: 'tax',
                featureName: 'triggerTaxUpdate',
              }) ||
                isFeatureEnabled({
                  moduleName: 'sourceManagement',
                  featureName: 'triggerSourceUpdates',
                })) && (
                <div className={classes.updateDataButton}>
                  <UpdateDataSourcesButton />
                </div>
              )}
            <IconButton
              className={classes.notificationsButton}
              aria-label="toggle notifications menu"
              size="small"
              aria-controls="menu-list-grow"
              aria-haspopup="true"
              id="top-nav-notifications-menu-button"
            >
              <NotificationsIcon className={classes.notificationsIcon} />
            </IconButton>
            <IconButton
              size="small"
              color="primary"
              aria-label="header menu"
              aria-controls="header-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
              className={classes.menuButton}
            >
              <MenuIcon className={classes.moreBtnIcon} />
            </IconButton>
            <Menu
              id="action-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleClose}
            >
              <MenuItem className={classes.menuItem} disableRipple>
                <List
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  dense
                  disablePadding
                  className={classes.menuList}
                >
                  {!hideAccountMenu && currentAccount.id && <AccountWindow />}
                  {!hideAccountMenu &&
                    currentAccount.inTrial &&
                    !currentAccount.trialEnded && (
                      <ListItem style={{ justifyContent: 'center' }}>
                        <TrialUpgradeButton variant="outlined" size="small" />
                      </ListItem>
                    )}
                  {isFeatureEnabled({
                    moduleName: 'client',
                    featureName: 'connectToAdmin',
                  }) && (
                    <ListItem>
                      <PendingCodeInviteButton />
                    </ListItem>
                  )}
                  {currentAccount &&
                    currentAccount.id &&
                    !currentAccount.disabled &&
                    !currentAccount.trialEnded && (
                      <>
                        {visibleListItems.map((tab) => (
                          <ListItemButton
                            key={tab.id}
                            className={classes.listItem}
                            onClick={() => {
                              if (tab.link) navigate({ to: tab.link });
                              handleClose();
                            }}
                          >
                            <ListItemIcon className={classes.listItemIcon}>
                              {tab.icon}
                            </ListItemIcon>
                            <ListItemText primary={tab.label} />
                          </ListItemButton>
                        ))}
                      </>
                    )}
                  {user && (
                    <ListItemButton
                      className={classes.listItem}
                      onClick={() => navigate({ to: '/user-settings' })}
                    >
                      <ListItemIcon className={classes.listItemIcon}>
                        <AccountBoxIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="User Settings" />
                    </ListItemButton>
                  )}
                  <ListItemButton
                    onClick={handleSupportListClick}
                    className={classes.listItem}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <HelpOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Support" />
                    {supportListOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={supportListOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense>
                      {supportListItem.map((item) => (
                        <ListItemButton
                          href={item.link}
                          component={item.link ? 'a' : 'div'}
                          target="_blank"
                          className={classes.nested}
                          onClick={() => {
                            if (!item.link) openZendeskWidget();
                            handleClose();
                          }}
                          key={item.title}
                        >
                          <ListItemIcon className={classes.listItemIcon}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.title} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                  {user && (
                    <ListItemButton
                      onClick={handleLogout}
                      className={classes.listItem}
                    >
                      <ListItemIcon className={classes.listItemIcon}>
                        <ExitToAppIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  )}
                </List>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </div>
      {currentAccount &&
        primaryAccountId === currentAccount.id &&
        !validTrialUrls.includes(pathname) &&
        !pathname.includes('/trial-expired') &&
        currentAccount.trialEnded && <Navigate to={`/trial-expired`} />}
    </AppBar>
  );
};

export default MobileHeader;

MobileHeader.propTypes = {
  setHeaderHeight: PropTypes.func.isRequired,
  hideAccountMenu: PropTypes.bool,
  hideUpdateButton: PropTypes.bool,
};

MobileHeader.defaultProps = {
  hideAccountMenu: false,
  hideUpdateButton: false,
};
